import { Injectable } from '@angular/core';
import { PDFDocument } from '../interfaces/pdf-document.interface';
import { AviProjection, AviRecord, AviReportResponse, AviRisk } from '../models/avi-report.model';

/** Native PDFKit port of the CPO AVI report template. */
const mm = (value: number): number => value * 72 / 25.4;
const PAGE_WIDTH = 595.28;
const PAGE_HEIGHT = 841.89;
const LEFT = mm(16);
const RIGHT = PAGE_WIDTH - LEFT;
const TOP = mm(32);
const BOTTOM = PAGE_HEIGHT - mm(20);
const COLORS = {
  ink: '#071b2b', navy: '#0b2130', cyan: '#00a7d8', muted: '#627686',
  line: '#d9e3ea', soft: '#f3f7fa', alternate: '#f8fbfd', white: '#ffffff'
};

interface Field { label: string; value: unknown; }
interface Chapter { number: number; title: string; }

@Injectable({ providedIn: 'root' })
export class AviPdfRendererService {
  private y = TOP;
  private avi!: AviProjection;

  renderDocument(doc: PDFDocument, response: AviReportResponse, coverImage: string): void {
    this.avi = response.avi;
    this.drawCover(doc, coverImage);

    // CPO's print stylesheet starts every chapter on a separate A4 page.
    this.startChapter(doc, { number: 1, title: 'Executive Assessment' });
    this.drawExecutiveAssessment(doc);
    this.startChapter(doc, { number: 2, title: 'Ownership & Control Structure' });
    this.drawOwnership(doc);
    this.startChapter(doc, { number: 3, title: 'Relationship & Evidence Register' });
    this.drawRelationshipRegister(doc);
    this.startChapter(doc, { number: 4, title: 'Control Findings & Key Individuals' });
    this.drawControlFindings(doc);
    this.startChapter(doc, { number: 5, title: 'Chronology & Material Developments' });
    this.drawChronology(doc);
    this.startChapter(doc, { number: 6, title: 'Risk & Incident History' });
    this.drawRiskHistory(doc);
    this.startChapter(doc, { number: 7, title: 'Sources & Assessment Method' });
    this.drawMethod(doc);
    this.startChapter(doc, { number: 8, title: 'Sources & Document References' });
    this.drawSources(doc);
    this.drawFooters(doc);
  }

  private drawCover(doc: PDFDocument, coverImage: string): void {
    doc.image(coverImage, 0, 0, { width: PAGE_WIDTH, height: PAGE_HEIGHT });
    const x = mm(18);
    doc.font('Helvetica-Bold').fontSize(43).fillColor('#f4f7f9')
      .text('AVI Module', x, mm(63), { width: mm(150), characterSpacing: -0.7 });
    doc.font('Helvetica-Bold').fontSize(18).fillColor('#b8d4ee')
      .text(`Vessel IMO ${this.display(this.avi.imo)}`, x, mm(80), { width: mm(150) });
  }

  private startChapter(doc: PDFDocument, chapter: Chapter): void {
    doc.addPage({ size: 'A4', margins: { top: 0, right: 0, bottom: 0, left: 0 } });
    this.drawHeader(doc);
    this.y = TOP;
    doc.font('Helvetica-Bold').fontSize(8).fillColor(COLORS.cyan)
      .text(`CHAPTER ${String(chapter.number).padStart(2, '0')}`, LEFT, this.y, { characterSpacing: 1.2 });
    this.y += 17;
    doc.font('Helvetica-Bold').fontSize(22).fillColor(COLORS.navy)
      .text(chapter.title, LEFT, this.y, { width: RIGHT - LEFT });
    this.y += 28;
  }

  /** Continuation pages retain CPO's running header, without an invented card. */
  private continuePage(doc: PDFDocument): void {
    doc.addPage({ size: 'A4', margins: { top: 0, right: 0, bottom: 0, left: 0 } });
    this.drawHeader(doc);
    this.y = TOP;
  }

  private drawHeader(doc: PDFDocument): void {
    doc.font('Times-Roman').fontSize(16).fillColor(COLORS.navy)
      .text('TEVUNA', LEFT, mm(14), { characterSpacing: 4.4 });
    doc.font('Helvetica').fontSize(7.2).fillColor(COLORS.muted)
      .text('AVI Module', RIGHT - mm(70), mm(14.2), { width: mm(70), align: 'right', characterSpacing: 0.45 });
    doc.font('Helvetica-Bold').fontSize(8.8).fillColor(COLORS.ink)
      .text(`Vessel IMO ${this.display(this.avi.imo)}`, RIGHT - mm(70), mm(18), { width: mm(70), align: 'right' });
    doc.rect(LEFT, mm(26.8), RIGHT - LEFT, mm(0.38)).fill(COLORS.navy);
  }

  private drawExecutiveAssessment(doc: PDFDocument): void {
    const group = this.groupData(this.avi.shipOwner);
    this.drawKpis(doc, [
      { label: 'Vessel / IMO', value: this.avi.imo },
      { label: 'Ship Owner', value: this.avi.shipOwner['name'] },
      { label: 'Ship Manager', value: this.avi.shipManager['name'] },
      { label: 'Ownership confidence', value: group.confidence }
    ]);
    this.drawFieldTable(doc, [
      { label: 'Supplied vessel name', value: this.avi.vesselName },
      { label: 'Ship Owner identified', value: this.avi.identification.shipownerIdentified ?? this.avi.identification['shipowner_identified'] },
      { label: 'Ship Manager identified', value: this.avi.identification.managerIdentified ?? this.avi.identification['manager_identified'] },
      { label: 'Ship Owner identification confidence', value: this.avi.identification.shipownerConfidenceLevel ?? this.avi.identification['shipowner_confidenceLevel'] },
      { label: 'Ship Manager identification confidence', value: this.avi.identification.shipmanagerConfidenceLevel ?? this.avi.identification['shipmanager_confidenceLevel'] },
      { label: 'Identification confidence', value: this.avi.identification.confidenceLevel },
      { label: 'Ownership research status', value: this.avi.identification.ownershipStatus ?? this.avi.identification['r12OwnershipStatus'] }
    ]);
    const events = this.events(this.avi.vesselRisk).length + this.avi.entityRisk.reduce((total, risk) => total + this.events(risk).length, 0);
    this.paragraph(doc, `${this.avi.ownership.length} corporate ownership / control records; ${this.avi.ubos.length} UBO records; ${events} vessel risk events; ${this.avi.entityRisk.length} entity risk assessment${this.avi.entityRisk.length === 1 ? '' : 's'}.`);
    this.subheading(doc, 'Vessel risk assessment');
    this.paragraph(doc, this.display(this.avi.vesselRisk.why));
  }

  private drawOwnership(doc: PDFDocument): void {
    const group = this.groupData(this.avi.shipOwner);
    this.paragraph(doc, `Group: ${this.display(group.name)} · Country: ${this.display(group.country)} · Confidence: ${this.display(group.confidence)}`, true);
    if (!this.avi.ownership.length) this.empty(doc, 'No supported corporate ownership / control records are supplied.');
    this.avi.ownership.forEach((record, index) => {
      this.subheading(doc, `Ownership record ${index + 1}`);
      this.drawFieldTable(doc, this.fields(record, [
        ['level', 'Ownership level'], ['nodeId', 'Node reference'], ['ownedNodeId', 'Owned node reference'],
        ['name', 'Name'], ['relationshipType', 'Relationship type'], ['ownershipPercentage', 'Direct ownership percentage'],
        ['asOfDate', 'As-of date'], ['country', 'Country'], ['actingFor', 'Acting for'], ['reasoning', 'Reasoning']
      ]));
    });
    this.paragraph(doc, 'Each arrow represents the typed relationship to the specified owned node. An unresolved immediate link is not completed by inference. UBO records are listed separately below.', true);
  }

  private drawRelationshipRegister(doc: PDFDocument): void {
    this.drawProfile(doc, 'Ship Owner', this.avi.shipOwner, false);
    this.drawProfile(doc, 'Ship Manager', this.avi.shipManager, true);
  }

  private drawProfile(doc: PDFDocument, title: string, party: AviRecord, manager: boolean): void {
    this.subheading(doc, title);
    this.drawFieldTable(doc, this.fields(party, [
      ['name', 'Identified name'], ['country', 'Country'], ['address', 'Address'], ['registrationAuthority', 'Registration authority'],
      ['localCode', 'Local registration code'], ['tevunaID', 'Tevuna ID']
    ]));
    this.subheading(doc, 'Company profile');
    const profile = this.companyData(party);
    this.drawPairedFieldTable(doc, this.fields(profile, [
      ['companyName', 'Company name'], ['aliases', 'Aliases'], ['country', 'Country'], ['address', 'Address'],
      ['city', 'City'], ['state', 'State / province'], ['zip', 'Postal code'], ['registrationNumber', 'Registration number'],
      ['incorporationDate', 'Incorporation date'], ['legalForm', 'Legal form'], ['industry', 'Industry'], ['sicCode', 'SIC code'],
      ['naicsCode', 'NAICS code'], ['ein', 'EIN'], ['website', 'Website'], ['email', 'Email'], ['phone', 'Phone'],
      ['estimatedEmployees', 'Estimated employees'], ['estimatedRevenue', 'Estimated revenue'], ['yearsInBusiness', 'Years in business'],
      ['creditScore', 'Credit score'], ['creditLimit', 'Credit limit']
    ]));
    if (!manager) return;
    const group = this.groupData(party);
    this.subheading(doc, 'Manager group structure');
    this.paragraph(doc, `Group: ${this.display(group.name)} · Country: ${this.display(group.country)} · Confidence: ${this.display(group.confidence)}`, true);
    const structure = this.companyData(party)['groupStructure'] as AviRecord | undefined;
    this.paragraph(doc, `Corporate ownership / control: ${this.display(structure?.['ownership'])}\nUBO records: ${this.display(structure?.['ubo'])}\nKey individuals: ${this.display(this.companyData(party)['individuals'])}`, true);
  }

  private drawControlFindings(doc: PDFDocument): void {
    this.subheading(doc, 'UBO records');
    if (!this.avi.ubos.length) this.empty(doc, 'No UBO records are supplied.');
    this.avi.ubos.forEach((ubo, index) => {
      this.subheading(doc, `${index + 1}. ${this.display(ubo['name'])}`);
      this.drawFieldTable(doc, this.fields(ubo, [['name', 'Name'], ['country', 'Country'], ['address', 'Address'], ['uboPercentage', 'Direct ownership percentage'], ['uboStartDate', 'UBO start date'], ['reasoning', 'Reasoning']]));
    });
    this.subheading(doc, 'Key individuals');
    if (!this.avi.individuals.length) this.empty(doc, 'No key individual records are supplied.');
    this.avi.individuals.forEach((individual, index) => {
      this.subheading(doc, `${index + 1}. ${this.display(individual['name'])}`);
      this.drawFieldTable(doc, this.fields(individual, [['name', 'Name'], ['country', 'Country'], ['address', 'Address'], ['role', 'Role']]));
    });
  }

  private drawChronology(doc: PDFDocument): void {
    this.drawTwoColumnTable(doc, 'Date / period', 'Record / development', this.chronology().map((entry) => [entry.date, entry.label]));
    this.paragraph(doc, 'The dates retain the precision supplied. Event descriptions, limitations and sources appear in the risk history. An as-of date is not treated as an ownership commencement date.', true);
  }

  private drawRiskHistory(doc: PDFDocument): void {
    this.drawRiskAssessment(doc, `Subject vessel: IMO ${this.display(this.avi.imo)}`, this.avi.vesselRisk);
    this.avi.entityRisk.forEach((risk, index) => this.drawRiskAssessment(doc, this.display(risk['entity_name'] ?? `Entity ${index + 1}`), risk));
  }

  private drawRiskAssessment(doc: PDFDocument, title: string, risk: AviRisk): void {
    this.subheading(doc, title);
    this.drawRiskStatus(doc, risk);
    this.paragraph(doc, this.display(risk.why));
    const assessedRecords = this.sourceList(risk['entity_refs']);
    if (assessedRecords.length) this.paragraph(doc, `Assessed records: ${assessedRecords.join(', ')}`, true);
    const events = this.events(risk);
    if (!events.length) this.empty(doc, 'No accepted material events are supplied for this assessment.');
    events.forEach((event, index) => this.drawRiskEvent(doc, event, index + 1));
    this.drawAssessmentSources(doc, risk.urls);
  }

  private drawRiskStatus(doc: PDFDocument, risk: AviRisk): void {
    const height = mm(11);
    this.ensure(doc, height);
    doc.rect(LEFT, this.y, RIGHT - LEFT, height).fill(COLORS.soft);
    doc.rect(LEFT, this.y, mm(1.1), height).fill(COLORS.cyan);
    doc.font('Helvetica').fontSize(8.5).fillColor(COLORS.ink).text('Risk:', LEFT + mm(4), this.y + mm(3.1));
    doc.font('Helvetica-Bold').fontSize(8.5).fillColor(COLORS.ink).text(this.display(risk.risk), LEFT + mm(13), this.y + mm(3.1));
    doc.font('Helvetica').fontSize(8.5).fillColor(COLORS.ink).text('Research status:', LEFT + mm(34), this.y + mm(3.1));
    doc.font('Helvetica-Bold').fontSize(8.5).fillColor(COLORS.ink).text(this.display(risk.research_status), LEFT + mm(64), this.y + mm(3.1));
    this.y += height + mm(3);
  }

  private drawRiskEvent(doc: PDFDocument, event: AviRecord, number: number): void {
    const description = this.display(event['description']);
    const fields: Field[] = [
      { label: 'Date precision', value: event['date_precision'] }, { label: 'Location', value: event['location_name'] },
      { label: 'Location precision', value: event['location_precision'] }, { label: 'Map coordinates', value: this.coordinates(event['geometry']) },
      { label: 'Location basis', value: event['location_basis'] }
    ];
    const sources = this.sourceList(event['source_urls']);
    this.ensure(doc, this.riskEventHeight(doc, description, fields, sources));
    doc.font('Helvetica-Bold').fontSize(10).fillColor(COLORS.ink).text(`${this.display(event['event_date'])} · Event ${number}`, LEFT, this.y, { width: RIGHT - LEFT });
    this.y += 16;
    this.paragraph(doc, description);
    fields.forEach((field) => this.drawMetaRow(doc, field));
    if (sources.length) {
      doc.font('Helvetica-Bold').fontSize(7.3).fillColor(COLORS.ink).text('Event sources', LEFT, this.y, { width: RIGHT - LEFT });
      this.y += 10;
      sources.forEach((source) => { doc.font('Helvetica').fontSize(6.8).fillColor(COLORS.muted).text(source, LEFT, this.y, { width: RIGHT - LEFT }); this.y += doc.heightOfString(source, { width: RIGHT - LEFT }) + 3; });
    }
    doc.rect(LEFT, this.y + mm(1), RIGHT - LEFT, mm(0.25)).fill(COLORS.line);
    this.y += mm(6);
  }

  private drawMethod(doc: PDFDocument): void {
    this.paragraph(doc, 'This report presents the supplied AVI data. Corporate ownership, control, registered vessel roles and ultimate beneficial ownership retain their separate meanings. Risk findings retain the source qualifications, dates and geographic precision supplied for each event.');
    this.paragraph(doc, 'N/A denotes an unavailable value; an empty section means that no records were supplied. These labels do not constitute a negative finding or clearance. Research status and confidence are shown independently from the risk result.');
    this.subheading(doc, 'Source URLs used for identification');
    this.drawAssessmentSources(doc, this.avi.sources);
    this.subheading(doc, 'Record references');
    this.paragraph(doc, 'Entity-risk references identify the assessed profile, corporate ownership occurrence or UBO record. The corresponding reference strings and Tevuna identifiers are preserved with their records.');
  }

  private drawSources(doc: PDFDocument): void {
    const sources = this.allSources();
    if (!sources.length) this.empty(doc, 'No source URLs supplied.');
    sources.forEach((source, index) => {
      const height = Math.max(mm(10), doc.heightOfString(source, { width: RIGHT - LEFT }) + mm(8));
      this.ensure(doc, height);
      doc.font('Helvetica-Bold').fontSize(8).fillColor(COLORS.ink).text(`[${index + 1}] ${this.host(source)}`, LEFT, this.y, { width: RIGHT - LEFT });
      this.y += 11;
      doc.font('Helvetica').fontSize(7.3).fillColor(COLORS.muted).text(source, LEFT, this.y, { width: RIGHT - LEFT });
      this.y += doc.heightOfString(source, { width: RIGHT - LEFT }) + mm(3);
      doc.rect(LEFT, this.y, RIGHT - LEFT, mm(0.25)).fill(COLORS.line);
      this.y += mm(3);
    });
  }

  private drawKpis(doc: PDFDocument, fields: Field[]): void {
    const gap = mm(3.2), width = (RIGHT - LEFT - gap) / 2, height = mm(22);
    fields.forEach((field, index) => {
      const x = LEFT + (index % 2) * (width + gap), y = this.y + Math.floor(index / 2) * (height + gap);
      doc.roundedRect(x, y, width, height, mm(1.6)).fill(COLORS.alternate);
      doc.rect(x, y, width, mm(1)).fill(COLORS.cyan);
      doc.font('Helvetica').fontSize(6.8).fillColor(COLORS.muted).text(field.label.toUpperCase(), x + mm(4), y + mm(4.2), { width: width - mm(8), characterSpacing: 0.5 });
      doc.font('Helvetica-Bold').fontSize(10.6).fillColor(COLORS.ink).text(this.display(field.value), x + mm(4), y + mm(8.5), { width: width - mm(8) });
    });
    this.y += height * 2 + gap + mm(5);
  }

  /** CPO field table: shaded label, white value, one pair per row. */
  private drawFieldTable(doc: PDFDocument, fields: Field[]): void {
    const labelWidth = (RIGHT - LEFT) * 0.28;
    fields.forEach((field, index) => {
      const height = this.singleRowHeight(doc, field, labelWidth, RIGHT - LEFT - labelWidth);
      this.ensure(doc, height);
      if (index % 2 === 1) doc.rect(LEFT + labelWidth, this.y, RIGHT - LEFT - labelWidth, height).fill(COLORS.alternate);
      doc.rect(LEFT, this.y, labelWidth, height).fill(COLORS.soft);
      this.drawCell(doc, field.label, LEFT + mm(3), this.y + mm(2.5), labelWidth - mm(6), true, 7.7);
      this.drawCell(doc, this.display(field.value), LEFT + labelWidth + mm(3), this.y + mm(2.5), RIGHT - LEFT - labelWidth - mm(6), false, 8.3);
      this.strokeRow(doc, LEFT, this.y, RIGHT - LEFT, height);
      this.y += height;
    });
    this.y += mm(4);
  }

  /** CPO company-profile layout: label/value/label/value. */
  private drawPairedFieldTable(doc: PDFDocument, fields: Field[]): void {
    const labelWidth = (RIGHT - LEFT) * 0.18, valueWidth = (RIGHT - LEFT) * 0.32;
    for (let index = 0; index < fields.length; index += 2) {
      const first = fields[index], second = fields[index + 1] ?? { label: '', value: '' };
      const height = Math.max(this.singleRowHeight(doc, first, labelWidth, valueWidth), this.singleRowHeight(doc, second, labelWidth, valueWidth));
      this.ensure(doc, height);
      doc.rect(LEFT, this.y, labelWidth, height).fill(COLORS.soft);
      doc.rect(LEFT + labelWidth + valueWidth, this.y, labelWidth, height).fill(COLORS.soft);
      this.drawCell(doc, first.label, LEFT + mm(2), this.y + mm(2), labelWidth - mm(4), true, 6.6);
      this.drawCell(doc, this.display(first.value), LEFT + labelWidth + mm(2), this.y + mm(2), valueWidth - mm(4), false, 7.1);
      this.drawCell(doc, second.label, LEFT + labelWidth + valueWidth + mm(2), this.y + mm(2), labelWidth - mm(4), true, 6.6);
      this.drawCell(doc, this.display(second.value), LEFT + labelWidth * 2 + valueWidth + mm(2), this.y + mm(2), valueWidth - mm(4), false, 7.1);
      this.strokeRow(doc, LEFT, this.y, RIGHT - LEFT, height);
      doc.rect(LEFT + labelWidth, this.y, mm(0.2), height).fill(COLORS.line);
      doc.rect(LEFT + labelWidth + valueWidth, this.y, mm(0.2), height).fill(COLORS.line);
      doc.rect(LEFT + labelWidth * 2 + valueWidth, this.y, mm(0.2), height).fill(COLORS.line);
      this.y += height;
    }
    this.y += mm(4);
  }

  private drawTwoColumnTable(doc: PDFDocument, firstHeader: string, secondHeader: string, rows: Array<[string, string]>): void {
    const firstWidth = (RIGHT - LEFT) * 0.22, headerHeight = mm(8);
    this.ensure(doc, headerHeight);
    doc.rect(LEFT, this.y, RIGHT - LEFT, headerHeight).fill(COLORS.navy);
    doc.font('Helvetica-Bold').fontSize(7.2).fillColor(COLORS.white).text(firstHeader, LEFT + mm(3), this.y + mm(2.5), { width: firstWidth - mm(5) });
    doc.font('Helvetica-Bold').fontSize(7.2).fillColor(COLORS.white).text(secondHeader, LEFT + firstWidth + mm(3), this.y + mm(2.5), { width: RIGHT - LEFT - firstWidth - mm(5) });
    this.y += headerHeight;
    rows.forEach(([first, second], index) => {
      const height = Math.max(mm(8), doc.heightOfString(second, { width: RIGHT - LEFT - firstWidth - mm(6) }) + mm(4));
      this.ensure(doc, height);
      if (index % 2 === 1) doc.rect(LEFT, this.y, RIGHT - LEFT, height).fill(COLORS.alternate);
      this.drawCell(doc, first, LEFT + mm(3), this.y + mm(2), firstWidth - mm(5), true, 8);
      this.drawCell(doc, second, LEFT + firstWidth + mm(3), this.y + mm(2), RIGHT - LEFT - firstWidth - mm(5), false, 8);
      this.strokeRow(doc, LEFT, this.y, RIGHT - LEFT, height);
      this.y += height;
    });
    this.y += mm(4);
  }

  private drawMetaRow(doc: PDFDocument, field: Field): void {
    const value = this.display(field.value), height = Math.max(12, doc.heightOfString(`${field.label}: ${value}`, { width: RIGHT - LEFT }) + 3);
    this.ensure(doc, height);
    doc.font('Helvetica-Bold').fontSize(7.5).fillColor(COLORS.ink).text(`${field.label}:`, LEFT, this.y, { width: mm(39) });
    doc.font('Helvetica').fontSize(7.5).fillColor(COLORS.muted).text(value, LEFT + mm(40), this.y, { width: RIGHT - LEFT - mm(40) });
    this.y += height;
  }

  private drawAssessmentSources(doc: PDFDocument, sources: string[]): void {
    const values = this.sourceList(sources);
    if (!values.length) return;
    const height = Math.max(mm(11), values.reduce((total, source) => total + doc.heightOfString(source, { width: RIGHT - LEFT }) + 3, 10));
    this.ensure(doc, height);
    doc.font('Helvetica-Bold').fontSize(7.4).fillColor(COLORS.ink).text('Assessment sources', LEFT, this.y, { width: RIGHT - LEFT });
    this.y += 10;
    values.forEach((source) => { doc.font('Helvetica').fontSize(6.8).fillColor(COLORS.muted).text(source, LEFT, this.y, { width: RIGHT - LEFT }); this.y += doc.heightOfString(source, { width: RIGHT - LEFT }) + 3; });
    this.y += mm(3);
  }

  private subheading(doc: PDFDocument, value: string): void {
    const height = doc.heightOfString(value, { width: RIGHT - LEFT }) + 9;
    this.ensure(doc, height);
    doc.font('Helvetica-Bold').fontSize(12.2).fillColor(COLORS.navy).text(value, LEFT, this.y, { width: RIGHT - LEFT });
    this.y += height;
  }

  private paragraph(doc: PDFDocument, value: string, small = false): void {
    const size = small ? 8.3 : 9.4;
    doc.font('Helvetica').fontSize(size);
    const height = doc.heightOfString(value, { width: RIGHT - LEFT, lineGap: 2 });
    this.ensure(doc, height + 7);
    doc.fillColor(COLORS.muted).text(value, LEFT, this.y, { width: RIGHT - LEFT, lineGap: 2 });
    this.y += height + 7;
  }

  private empty(doc: PDFDocument, value: string): void {
    this.ensure(doc, 18);
    doc.font('Helvetica-Oblique').fontSize(8.3).fillColor(COLORS.muted).text(value, LEFT, this.y, { width: RIGHT - LEFT });
    this.y += 18;
  }

  private ensure(doc: PDFDocument, requiredHeight: number): void { if (this.y + requiredHeight > BOTTOM) this.continuePage(doc); }
  private drawCell(doc: PDFDocument, text: string, x: number, y: number, width: number, label: boolean, size: number): void { doc.font(label ? 'Helvetica-Bold' : 'Helvetica').fontSize(size).fillColor(label ? COLORS.navy : COLORS.ink).text(text, x, y, { width, lineGap: 1 }); }
  private strokeRow(doc: PDFDocument, x: number, y: number, width: number, height: number): void { doc.rect(x, y, width, height).lineWidth(mm(0.25)).strokeColor(COLORS.line).stroke(); }
  private singleRowHeight(doc: PDFDocument, field: Field, labelWidth: number, valueWidth: number): number {
    doc.font('Helvetica-Bold').fontSize(7.7); const labelHeight = doc.heightOfString(field.label, { width: labelWidth - mm(6), lineGap: 1 });
    doc.font('Helvetica').fontSize(8.3); const valueHeight = doc.heightOfString(this.display(field.value), { width: valueWidth - mm(6), lineGap: 1 });
    return Math.max(mm(8.8), Math.max(labelHeight, valueHeight) + mm(5));
  }
  private riskEventHeight(doc: PDFDocument, description: string, fields: Field[], sources: string[]): number {
    doc.font('Helvetica').fontSize(9.4); let height = 16 + doc.heightOfString(description, { width: RIGHT - LEFT, lineGap: 2 }) + 7;
    fields.forEach((field) => { height += Math.max(12, doc.heightOfString(`${field.label}: ${this.display(field.value)}`, { width: RIGHT - LEFT }) + 3); });
    if (sources.length) height += 10 + sources.reduce((total, source) => total + doc.heightOfString(source, { width: RIGHT - LEFT }) + 3, 0);
    return height + mm(6);
  }

  private chronology(): Array<{ date: string; label: string }> {
    const entries: Array<{ date: string; label: string }> = [];
    const add = (date: unknown, label: string): void => { if (date) entries.push({ date: this.display(date), label }); };
    this.events(this.avi.vesselRisk).forEach((event, index) => add(event['event_date'], `Vessel risk event ${index + 1}`));
    this.avi.entityRisk.forEach((risk, riskIndex) => this.events(risk).forEach((event, eventIndex) => add(event['event_date'], `${this.display(risk['entity_name'] ?? `Entity ${riskIndex + 1}`)}: event ${eventIndex + 1}`)));
    return entries.sort((first, second) => first.date.localeCompare(second.date));
  }

  private companyData(party: AviRecord): AviRecord { const value = party['vesselData']; if (value && typeof value === 'object' && !Array.isArray(value)) return value as AviRecord; const fallback = party['tevunaData']; return fallback && typeof fallback === 'object' && !Array.isArray(fallback) ? fallback as AviRecord : {}; }
  private groupData(party: AviRecord): { name: unknown; country: unknown; confidence: unknown } { const structure = this.companyData(party)['groupStructure'] as AviRecord | undefined; const group = structure?.['groupID'] as AviRecord | undefined; return { name: group?.['name'] ?? this.avi.group['name'], country: group?.['country'] ?? this.avi.group['country'], confidence: structure?.['groupUpConfidenceLevel'] ?? this.avi.group['confidence'] }; }
  private fields(record: AviRecord, entries: Array<[string, string]>): Field[] { return entries.map(([key, label]) => ({ label, value: record[key] })); }
  private events(risk: AviRisk): AviRecord[] { return Array.isArray(risk.events) ? risk.events : []; }
  private sourceList(value: unknown): string[] { return Array.isArray(value) ? value.map((item) => String(item)).filter(Boolean) : []; }
  private coordinates(value: unknown): string {
    if (!value || typeof value !== 'object') return 'N/A';
    const geometry = value as AviRecord;
    const coordinates = geometry['coordinates'];
    if (!Array.isArray(coordinates)) return 'N/A';
    const type = geometry['type'] ? `${this.display(geometry['type'])} · ` : '';
    return `${type}Longitude ${this.display(coordinates[0])} · Latitude ${this.display(coordinates[1])}`;
  }
  private allSources(): string[] { const sources = new Set<string>(this.avi.sources); [this.avi.vesselRisk, ...this.avi.entityRisk].forEach((risk) => risk.urls.forEach((source) => sources.add(source))); return [...sources]; }
  private host(value: string): string { try { return new URL(value).hostname || value; } catch { return value; } }
  private display(value: unknown): string { if (value === null || value === undefined || value === '') return 'N/A'; if (Array.isArray(value)) return value.length ? value.map((item) => this.display(item)).join(', ') : 'No records'; if (typeof value === 'boolean') return value ? 'Yes' : 'No'; if (typeof value === 'object') return 'N/A'; return String(value); }

  private drawFooters(doc: PDFDocument): void {
    const pages = doc.bufferedPageRange();
    for (let index = 1; index < pages.count; index++) {
      doc.switchToPage(index);
      doc.font('Helvetica').fontSize(6.3).fillColor(COLORS.muted).text('Tevuna Insights LLC · Advanced Vessel Intelligence', LEFT, PAGE_HEIGHT - mm(10), { width: mm(110) });
      doc.text(`${index + 1} / ${pages.count}`, RIGHT - mm(20), PAGE_HEIGHT - mm(10), { width: mm(20), align: 'right' });
    }
  }
}
